/**
 * @fileoverview Dismissable onboarding / welcome checklist for dashboards
 * @module ctl-onboarding-checklist
 * @version 1.0.0
 *
 * @description
 * An onboarding checklist panel that guides new users through initial setup
 * steps. Each item can be checked off individually. The panel can be dismissed
 * (hidden) once all items are complete, or manually by clicking the dismiss
 * button. Remembers dismissed state in localStorage when a storage-key is set.
 *
 * @example
 * <ctl-onboarding-checklist
 *   title="Welcome to the Dashboard 👋"
 *   subtitle="Complete these steps to get started."
 *   storage-key="dashboard-onboarding"
 *   items='[
 *     {"id":"profile","label":"Complete your profile","done":true},
 *     {"id":"invite","label":"Invite a team member"},
 *     {"id":"data","label":"Connect your data source"},
 *     {"id":"widget","label":"Add your first dashboard widget"}
 *   ]'>
 * </ctl-onboarding-checklist>
 */

import { safeJsonParse } from '../../../utils/safeJsonParse.js';
import "../foundation/ctl-button.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host {
    display: block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  :host([dismissed]) { display: none; }

  .card {
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-card);
    overflow: hidden;
    border-top: 3px solid var(--ctl-color-brand);
  }

  .head {
    padding: var(--ctl-space-4) var(--ctl-space-4) var(--ctl-space-2-5);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--ctl-space-3);
  }

  .head-left { flex: 1; min-width: 0; }

  .title {
    font-size: var(--ctl-font-size-xl);
    font-weight: var(--ctl-font-weight-extrabold);
    line-height: 1.2;
    letter-spacing: -0.01em;
    margin: 0 0 var(--ctl-space-1-5);
  }

  .subtitle {
    font-size: var(--ctl-font-size-base);
    color: var(--ctl-color-muted);
    margin: 0;
    line-height: 1.4;
  }

  .dismiss-btn {
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--ctl-radius-md);
    color: var(--ctl-color-muted);
    flex-shrink: 0;
    transition: background var(--ctl-transition-fast, 150ms), color var(--ctl-transition-fast, 150ms);
    font-size: var(--ctl-font-size-body);
    line-height: 1;
  }

  .dismiss-btn:hover {
    background: color-mix(in srgb, var(--ctl-color-muted) 12%, var(--ctl-color-surface));
    color: var(--ctl-color-text);
  }

  .dismiss-btn:focus-visible {
    outline: 2px solid var(--ctl-color-brand);
    outline-offset: 2px;
  }

  .progress-bar-wrap {
    padding: 0 var(--ctl-space-4) var(--ctl-space-2-5);
  }

  .progress-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--ctl-space-1-5);
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
  }

  .progress-track {
    height: 6px;
    background: color-mix(in srgb, var(--ctl-color-brand) 14%, var(--ctl-color-surface));
    border-radius: var(--ctl-radius-full);
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--ctl-color-brand);
    border-radius: var(--ctl-radius-full);
    transition: width 300ms ease;
  }

  .items {
    padding: var(--ctl-space-1) var(--ctl-space-4) var(--ctl-space-4);
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-1);
  }

  .item {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-3);
    padding: var(--ctl-space-2-5) var(--ctl-space-3);
    border-radius: var(--ctl-radius-md);
    cursor: pointer;
    transition: background var(--ctl-transition-fast, 150ms);
    user-select: none;
  }

  .item:hover {
    background: color-mix(in srgb, var(--ctl-color-brand) 6%, var(--ctl-color-surface));
  }

  .item:focus-visible {
    outline: 2px solid var(--ctl-color-brand);
    outline-offset: 1px;
  }

  .check {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid var(--ctl-color-border);
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 200ms, background 200ms;
    font-size: var(--ctl-font-size-xs);
    color: transparent;
  }

  .item[aria-checked="true"] .check {
    background: var(--ctl-color-brand);
    border-color: var(--ctl-color-brand);
    color: var(--ctl-color-on-brand, #fff);
  }

  .item-label {
    flex: 1;
    font-size: var(--ctl-font-size-base);
    line-height: 1.3;
    transition: opacity 200ms;
  }

  .item[aria-checked="true"] .item-label {
    opacity: 0.5;
    text-decoration: line-through;
  }

  .item-link {
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-brand);
    text-decoration: underline;
    text-underline-offset: 2px;
    flex-shrink: 0;
    display: none;
  }

  .item:hover .item-link,
  .item:focus-visible .item-link { display: block; }

  .complete-banner {
    margin: 0 var(--ctl-space-4) var(--ctl-space-4);
    padding: var(--ctl-space-3);
    background: color-mix(in srgb, var(--ctl-color-success) 10%, var(--ctl-color-surface));
    border: 1px solid color-mix(in srgb, var(--ctl-color-success) 25%, var(--ctl-color-surface));
    border-radius: var(--ctl-radius-md);
    display: flex;
    align-items: center;
    gap: var(--ctl-space-3);
    display: none;
  }

  .complete-banner[data-show] { display: flex; }

  .complete-icon {
    font-size: var(--ctl-font-size-xl-plus);
    flex-shrink: 0;
  }

  .complete-text {
    flex: 1;
    font-size: var(--ctl-font-size-base);
    color: var(--ctl-color-success);
    font-weight: var(--ctl-font-weight-semibold);
  }
</style>

<div class="card" part="card" role="region" aria-labelledby="ctl-title">
  <div class="head" part="head">
    <div class="head-left">
      <div class="title" id="title"></div>
      <div class="subtitle" id="subtitle"></div>
    </div>
    <button class="dismiss-btn" id="dismissBtn" aria-label="Dismiss onboarding checklist" title="Dismiss">✕</button>
  </div>

  <div class="progress-bar-wrap">
    <div class="progress-meta">
      <span id="progressLabel">0 of 0 complete</span>
      <span id="progressPct">0%</span>
    </div>
    <div class="progress-track" role="progressbar" id="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
      <div class="progress-fill" id="progressFill" style="width:0%"></div>
    </div>
  </div>

  <div class="items" id="items" role="list"></div>

  <div class="complete-banner" id="completeBanner" part="complete-banner" role="status">
    <span class="complete-icon">🎉</span>
    <span class="complete-text" id="completeText">All steps complete! You're all set.</span>
  </div>
</div>
`;

export class CtlOnboardingChecklist extends HTMLElement {
  static get observedAttributes() {
    return ["title", "subtitle", "items", "dismissed", "complete-message", "storage-key"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._titleEl = this.shadowRoot.getElementById("title");
    this._subtitleEl = this.shadowRoot.getElementById("subtitle");
    this._itemsEl = this.shadowRoot.getElementById("items");
    this._progressLabel = this.shadowRoot.getElementById("progressLabel");
    this._progressPct = this.shadowRoot.getElementById("progressPct");
    this._progressFill = this.shadowRoot.getElementById("progressFill");
    this._progressbar = this.shadowRoot.getElementById("progressbar");
    this._completeBanner = this.shadowRoot.getElementById("completeBanner");
    this._completeText = this.shadowRoot.getElementById("completeText");
    this._dismissBtn = this.shadowRoot.getElementById("dismissBtn");

    this._items = [];
    this._onDismiss = this._onDismiss.bind(this);
  }

  connectedCallback() {
    // Restore dismissed state from storage
    const key = this.storageKey;
    if (key && this._storage("get", key) === "dismissed") {
      this.setAttribute("dismissed", "");
      return;
    }

    this._parse();
    this._dismissBtn.addEventListener("click", this._onDismiss);
    this._bindItemEvents();
    this._render();
  }

  disconnectedCallback() {
    this._dismissBtn.removeEventListener("click", this._onDismiss);
  }

  /** Bind item toggle events once using delegation on the items container. */
  _bindItemEvents() {
    const toggle = (e) => {
      // Don't toggle when clicking a link
      if (e.target.tagName === "A") return;
      const item = e.target.closest(".item[data-id]");
      if (!item) return;
      this._toggleItem(item.dataset.id);
    };
    this._itemsEl.addEventListener("click", toggle);
    this._itemsEl.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      if (e.target.tagName === "A") return;
      const item = e.target.closest(".item[data-id]");
      if (!item) return;
      e.preventDefault();
      this._toggleItem(item.dataset.id);
    });
  }

  attributeChangedCallback(name) {
    if (name === "items") this._parse();
    this._render();
  }

  get title() { return this.getAttribute("title") || ""; }
  set title(v) { this.setAttribute("title", String(v ?? "")); }

  get subtitle() { return this.getAttribute("subtitle") || ""; }
  set subtitle(v) { this.setAttribute("subtitle", String(v ?? "")); }

  get dismissed() { return this.hasAttribute("dismissed"); }
  set dismissed(v) { v ? this.setAttribute("dismissed", "") : this.removeAttribute("dismissed"); }

  get storageKey() { return this.getAttribute("storage-key") || ""; }
  set storageKey(v) { this.setAttribute("storage-key", String(v ?? "")); }

  get completeMessage() { return this.getAttribute("complete-message") || "All steps complete! You're all set."; }

  get items() { return this._items; }
  set items(v) {
    this._items = Array.isArray(v) ? v : [];
    this.setAttribute("items", JSON.stringify(this._items));
  }

  _parse() {
    const raw = this.getAttribute("items");
    if (raw) {
      const parsed = safeJsonParse(raw, []);
      if (Array.isArray(parsed)) this._items = parsed;
    }
  }

  _onDismiss() {
    this.setAttribute("dismissed", "");
    const key = this.storageKey;
    if (key) this._storage("set", key, "dismissed");

    this.dispatchEvent(new CustomEvent("ctl-dismiss", { bubbles: true, composed: true }));
  }

  _toggleItem(id) {
    this._items = this._items.map(it =>
      it.id === id ? { ...it, done: !it.done } : it
    );
    this._renderItems();
    this._renderProgress();

    this.dispatchEvent(new CustomEvent("ctl-item-change", {
      bubbles: true,
      composed: true,
      detail: { items: [...this._items], item: this._items.find(it => it.id === id) }
    }));

    // Auto-dismiss after short delay when all complete
    const allDone = this._items.every(it => it.done);
    if (allDone) {
      this._completeBanner.setAttribute("data-show", "");
      this._completeText.textContent = this.completeMessage;
    } else {
      this._completeBanner.removeAttribute("data-show");
    }
  }

  _render() {
    this._titleEl.textContent = this.title;
    this._subtitleEl.textContent = this.subtitle;
    this._renderItems();
    this._renderProgress();
  }

  /** Update items HTML only; event handling is delegated via _bindItemEvents(). */
  _renderItems() {
    const items = this._items;
    this._itemsEl.innerHTML = items.map(it => {
      const done = !!it.done;
      const label = it.label || it.id || "";
      const link = it.link || "";
      return `
        <div class="item" role="listitem" aria-checked="${done}" tabindex="0" data-id="${this._esc(String(it.id || ""))}">
          <div class="check" aria-hidden="true">${done ? "✓" : ""}</div>
          <div class="item-label">${this._esc(label)}</div>
          ${link ? `<a class="item-link" href="${this._esc(link)}" target="_blank" rel="noopener noreferrer" tabindex="-1" aria-label="Learn more about ${this._esc(label)}">Set up →</a>` : ""}
        </div>
      `;
    }).join("");
  }

  _renderProgress() {
    const items = this._items;
    const total = items.length;
    const done = items.filter(it => it.done).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

    this._progressLabel.textContent = `${done} of ${total} complete`;
    this._progressPct.textContent = `${pct}%`;
    this._progressFill.style.width = `${pct}%`;
    this._progressbar.setAttribute("aria-valuenow", pct);
  }

  _storage(op, key, val) {
    try {
      if (op === "get") return localStorage.getItem(key);
      if (op === "set") localStorage.setItem(key, val);
    } catch {
      // Storage not available (private mode, etc.)
    }
    return null;
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }
}

if (!customElements.get("ctl-onboarding-checklist")) {
  customElements.define("ctl-onboarding-checklist", CtlOnboardingChecklist);
}
